import axios from "axios";
import { apiErrorMessage } from "./apiErrorMessage";

// Mismo-origen salvo que se defina una base explícita.
const API_BASE = process.env.REACT_APP_API_BASE_URL || "";
const EMAIL_DOMAIN = "conductores.alasapi.com";

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("access_token");
      // No redirigir aquí: App.js maneja el estado de sesión.
    }
    // Motivo entendible para el conductor: axios deja
    // error.message = "Request failed with status code NNN", y si el backend
    // manda detail como objeto, pintarlo en pantalla tumbaba la vista
    // (React #31, pantalla en blanco). Aquí siempre queda texto plano.
    if (!error.response) error.isNetworkError = true;
    try {
      const humano = apiErrorMessage(error);
      if (humano) { error.rawMessage = error.message; error.message = humano; }
    } catch (_) { /* traducir jamás debe romper el manejo del error */ }
    return Promise.reject(error);
  }
);

// ---- Sesión ----
export function getToken() {
  return localStorage.getItem("access_token");
}

export function logout() {
  localStorage.removeItem("access_token");
}

/** Decodifica el payload del JWT (sin verificar firma) para leer "sub". */
export function getUserIdFromToken() {
  const token = getToken();
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const sub = payload.sub;
    return sub != null ? parseInt(sub, 10) : null;
  } catch {
    return null;
  }
}

/** Login del conductor: cédula + contraseña (la contraseña es la cédula). */
export async function login(cedula, password) {
  const email = `${String(cedula).trim()}@${EMAIL_DOMAIN}`;
  const { data } = await api.post("/auth/login", { email, password });
  const token = data?.access_token || data?.token;
  if (!token) throw new Error("Respuesta de login inválida");
  localStorage.setItem("access_token", token);
  return token;
}

// ---- Despacho del conductor ----
export async function getMyDispatch() {
  const { data } = await api.get("/api/fleet/driver/my-dispatch");
  return data;
}

// ---- Mis datos y documentos personales ----
export async function getMyProfile() {
  const { data } = await api.get("/api/fleet/driver/profile");
  return data;
}

export async function getMyDocuments() {
  const { data } = await api.get("/api/fleet/driver/documents");
  return data;
}

export async function uploadMyDocument(documentType, file) {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await api.post(
    `/api/fleet/driver/documents/${encodeURIComponent(documentType)}`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 },
  );
  return data;
}

export async function downloadMyDocument(filename) {
  const { data } = await api.get(
    `/api/fleet/driver/documents/${encodeURIComponent(filename)}`,
    { responseType: "blob" },
  );
  return data;
}

export async function deleteMyDocument(documentType) {
  const { data } = await api.delete(
    `/api/fleet/driver/documents/${encodeURIComponent(documentType)}`,
  );
  return data;
}

// ---- Recorrido GPS en vivo (GeoNex, vehículo derivado de la sesión) ----
export async function getMyLiveLocation() {
  const { data } = await api.get("/api/fleet/driver/live-location");
  return data;
}

// ---- Llave Dallas virtual (sesión GeoNex aislada en cookie HttpOnly) ----
export async function getGeoNexSession() {
  const { data } = await api.get("/api/fleet/driver/geonex/session");
  return data;
}

export async function connectGeoNex(password) {
  const { data } = await api.post("/api/fleet/driver/geonex/login", { password });
  return data;
}

export async function disconnectGeoNex() {
  const { data } = await api.delete("/api/fleet/driver/geonex/session");
  return data;
}

export async function changeGeoNexPassword(newPassword) {
  const { data } = await api.post("/api/fleet/driver/geonex/change-password", {
    new_password: newPassword,
  });
  return data;
}

export async function geonexCheckin(payload) {
  const { data } = await api.post("/api/fleet/driver/geonex/checkin", payload);
  return data;
}

export async function geonexCheckout() {
  const { data } = await api.post("/api/fleet/driver/geonex/checkout");
  return data;
}

// ---- Estado de preoperacional del vehículo (validez 24h) ----
export async function getVehicleStatus(vehicleId) {
  const { data } = await api.get(`/api/fleet/vehicle-preoperationals/${vehicleId}/status`);
  return data;
}

// ---- Formulario de preoperacional (igual que taquilla) ----
export async function getPreoperationalForm(type = "daily") {
  const { data: config } = await api.get(
    `/api/preoperacionales/preoperational/by_type/?type=${type}`
  );
  if (!config || !config.id) {
    throw new Error("No hay configuración de preoperacional diaria.");
  }
  const { data: groups } = await api.get(
    `/api/preoperacionales/groups/by_preoperational/?preoperational_id=${config.id}`
  );
  const { data: questions } = await api.get(
    `/api/preoperacionales/questions/by_preoperational/?preoperational_id=${config.id}`
  );

  const groupsWithQuestions = (groups || [])
    .slice()
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((g) => ({
      ...g,
      questions: (questions || [])
        .filter((q) => q.group_id === g.id)
        .sort((a, b) => (a.order || 0) - (b.order || 0)),
    }));

  return { config, groups: groupsWithQuestions };
}

// ---- Crear preoperacional ----
export async function createPreoperational(payload) {
  const { data } = await api.post("/api/fleet/vehicle-preoperationals/", payload);
  return data;
}

// ---- Foto de una NO CONFORME (req #104): va al hallazgo que creó la preoperacional ----
export async function uploadDefectEvidence(defectId, file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post(`/api/fleet/vehicle-defects/${defectId}/evidence?kind=reported`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  });
  return data;
}

// ---- Mantenimientos (requerimiento #97) ----
export async function getMyMaintenance() {
  const { data } = await api.get("/api/fleet/driver/maintenance");
  return data;
}

/** Datos + evidencia en un solo envío: o queda completo o no queda nada. */
export async function createDriverMaintenance(formData) {
  const { data } = await api.post("/api/fleet/driver/maintenance", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 180000,
  });
  return data;
}

export default api;
